import type { CalendarEvent, EventKind } from "@ronin/contracts";
import {
  Button,
  Checkbox,
  DateInput,
  Dialog,
  Input,
  Kbd,
  Label,
  SegmentedControl,
  Textarea,
  TimeInput,
} from "@ronin/ui";
import { Trash2 } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useId, useState } from "react";
import {
  type EventFormValues,
  formError,
  moveStart,
  newEventValues,
  toEventInput,
  valuesFromEvent,
} from "./event-form.js";

const kinds: ReadonlyArray<{ value: EventKind; label: string }> = [
  { value: "event", label: "Event" },
  { value: "birthday", label: "Birthday" },
];

export type EventDialogState =
  | { mode: "closed" }
  | { mode: "create"; date: string; time?: string }
  | { mode: "edit"; event: CalendarEvent };

interface EventDialogProps {
  state: EventDialogState;
  timeZone: string;
  onClose: () => void;
  onSubmit: (input: ReturnType<typeof toEventInput>, event?: CalendarEvent) => Promise<unknown>;
  onDelete: (event: CalendarEvent) => void;
}

/** Add and edit own events (spec03 FR7), like the todo dialog. */
export function EventDialog({ state, timeZone, onClose, onSubmit, onDelete }: EventDialogProps) {
  const open = state.mode !== "closed";
  const key =
    state.mode === "edit"
      ? state.event.id
      : state.mode === "create"
        ? `new-${state.date}-${state.time ?? ""}`
        : "closed";
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={state.mode === "edit" ? "Edit event" : "New event"}
    >
      {state.mode !== "closed" && (
        <EventForm
          key={key}
          initial={
            state.mode === "edit"
              ? valuesFromEvent(state.event, timeZone)
              : newEventValues(state.date, state.time, timeZone)
          }
          event={state.mode === "edit" ? state.event : undefined}
          timeZone={timeZone}
          onCancel={onClose}
          onSubmit={onSubmit}
          onDelete={onDelete}
        />
      )}
    </Dialog>
  );
}

function EventForm({
  initial,
  event,
  timeZone,
  onCancel,
  onSubmit,
  onDelete,
}: {
  initial: EventFormValues;
  event?: CalendarEvent;
  timeZone: string;
  onCancel: () => void;
  onSubmit: EventDialogProps["onSubmit"];
  onDelete: (event: CalendarEvent) => void;
}) {
  const id = useId();
  const [values, setValues] = useState(initial);
  const [pending, setPending] = useState(false);
  const set = <K extends keyof EventFormValues>(key: K, value: EventFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const error = formError(values, timeZone);
  const birthday = values.kind === "birthday";
  const canSave = !error && !pending;

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSave) return;
    setPending(true);
    try {
      await onSubmit(toEventInput(values, timeZone), event);
    } catch {
      // The error toast comes from the mutation; keep the dialog open with the input.
    } finally {
      setPending(false);
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} onKeyDown={onKeyDown}>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-title`}>Title</Label>
        <Input
          id={`${id}-title`}
          value={values.title}
          maxLength={200}
          placeholder={birthday ? "e.g. Anna" : "e.g. Dentist"}
          data-autofocus
          onChange={(e) => set("title", e.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div className="flex flex-col gap-1">
          <span id={`${id}-kind`} className="text-xs font-medium text-muted-foreground">
            Kind
          </span>
          <SegmentedControl
            aria-labelledby={`${id}-kind`}
            value={values.kind}
            options={kinds}
            onValueChange={(kind) => set("kind", kind)}
          />
        </div>
        {!birthday && (
          <div className="flex h-8 items-center gap-2">
            <Checkbox
              id={`${id}-allday`}
              checked={values.allDay}
              onCheckedChange={(checked) => set("allDay", checked === true)}
            />
            <Label htmlFor={`${id}-allday`} className="text-sm text-secondary-foreground">
              All day
            </Label>
          </div>
        )}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">
          {birthday ? "Date" : "When"}
        </legend>
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={`${id}-start-date`} className="sr-only">
            {birthday ? "Date" : "Start date"}
          </Label>
          <DateInput
            id={`${id}-start-date`}
            className="w-40"
            value={values.startDate}
            onValueChange={(value) =>
              setValues((v) => moveStart(v, { startDate: value }, timeZone))
            }
          />
          {!birthday && !values.allDay && (
            <>
              <Label htmlFor={`${id}-start-time`} className="sr-only">
                Start time
              </Label>
              <TimeInput
                id={`${id}-start-time`}
                className="w-28"
                value={values.startTime}
                onValueChange={(value) =>
                  setValues((v) => moveStart(v, { startTime: value }, timeZone))
                }
              />
            </>
          )}
        </div>
        {!birthday && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-6 text-xs text-dim-foreground" aria-hidden>
              to
            </span>
            <Label htmlFor={`${id}-end-date`} className="sr-only">
              End date
            </Label>
            <DateInput
              id={`${id}-end-date`}
              className="w-40"
              value={values.endDate}
              onValueChange={(value) => set("endDate", value)}
            />
            {!values.allDay && (
              <>
                <Label htmlFor={`${id}-end-time`} className="sr-only">
                  End time
                </Label>
                <TimeInput
                  id={`${id}-end-time`}
                  className="w-28"
                  value={values.endTime}
                  onValueChange={(value) => set("endTime", value)}
                />
              </>
            )}
          </div>
        )}
        {error && values.title.trim() !== "" && (
          <p role="status" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </fieldset>

      {birthday ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-birth-year`}>
            Birth year{" "}
            <span className="font-normal text-faint-foreground">(optional, shows the age)</span>
          </Label>
          <Input
            id={`${id}-birth-year`}
            inputMode="numeric"
            maxLength={4}
            className="w-28"
            placeholder="e.g. 1996"
            value={values.birthYear}
            onChange={(e) => set("birthYear", e.target.value.replace(/\D/g, ""))}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-location`}>
            Location <span className="font-normal text-faint-foreground">(optional)</span>
          </Label>
          <Input
            id={`${id}-location`}
            value={values.location}
            maxLength={200}
            placeholder="e.g. Wien"
            onChange={(e) => set("location", e.target.value)}
          />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-description`}>
          Description <span className="font-normal text-faint-foreground">(optional)</span>
        </Label>
        <Textarea
          id={`${id}-description`}
          value={values.description}
          maxLength={5000}
          placeholder="Add details…"
          onChange={(e) => set("description", e.target.value)}
        />
      </div>

      <div className="mt-2 flex items-center gap-2">
        {event && (
          <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(event)}>
            <Trash2 />
            Delete
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSave}>
            {event ? "Save" : "Create event"}
            <Kbd className="hidden sm:inline-flex">⌘↵</Kbd>
          </Button>
        </div>
      </div>
    </form>
  );
}
